// Delay padrão reduzido - scripts carregam após interação OU após esse tempo
const DEFER_TO_LOAD: number = 7300;

export function inject(innerHTML: string) {
  if (!innerHTML) {
    return {};
  }

  return {
    type: 'text/javascript',
    innerHTML,
  };
}

/**
 * Carrega script após interação do usuário OU após o delay especificado
 * Usa requestIdleCallback para não bloquear a thread principal
 */
export function load(scriptRaw: string, loadDelay: number = DEFER_TO_LOAD) {
  return {
    type: 'text/javascript',
    innerHTML: `
      (function() {
        var loaded = false;
        var loadScript = function() {
          if (loaded) return;
          loaded = true;

          // Usa requestIdleCallback se disponível, senão setTimeout
          var scheduleLoad = window.requestIdleCallback || function(cb) {
            setTimeout(cb, 1);
          };

          scheduleLoad(function() {
            ${scriptRaw}
          });
        };

        // Carrega após interação do usuário (scroll, click, mousemove, touchstart)
        var events = ['scroll', 'click', 'mousemove', 'touchstart', 'keydown'];
        var onInteraction = function() {
          loadScript();
          events.forEach(function(e) {
            window.removeEventListener(e, onInteraction);
          });
        };

        events.forEach(function(e) {
          window.addEventListener(e, onInteraction, { once: true, passive: true });
        });

        // Fallback: carrega após ${loadDelay}ms se não houver interação
        setTimeout(loadScript, ${loadDelay});
      })();
    `,
    defer: true,
  };
}

/**
 * Carrega imediatamente após requestIdleCallback (sem delay)
 */
export function fastLoad(scriptRaw: string) {
  return {
    type: 'text/javascript',
    innerHTML: `
      (function() {
        var scheduleLoad = window.requestIdleCallback || function(cb) {
          setTimeout(cb, 1);
        };

        scheduleLoad(function() {
          ${scriptRaw}
        });
      })();
    `,
    defer: true,
  };
}

export function conditionalLoad(condition: boolean, scriptRaw: string) {
  if (!condition) {
    return {};
  }

  return load(scriptRaw);
}

export function conditionalFastLoad(condition: boolean, scriptRaw: string) {
  if (!condition) {
    return {};
  }

  return fastLoad(scriptRaw);
}

/**
 * Para scripts MUITO pesados (>100KB) como Altenar (278KB)
 * Carrega APENAS após interação + delay maior
 */
export function loadHeavy(scriptRaw: string, minDelay: number = 10000) {
  return {
    type: 'text/javascript',
    innerHTML: `
      (function() {
        var loaded = false;
        var startTime = Date.now();

        var loadScript = function() {
          if (loaded) return;
          var elapsed = Date.now() - startTime;
          if (elapsed < ${minDelay}) return; // Força delay mínimo

          loaded = true;

          var scheduleLoad = window.requestIdleCallback || function(cb) {
            setTimeout(cb, 1);
          };

          scheduleLoad(function() {
            ${scriptRaw}
          }, { timeout: 3000 });
        };

        // Carrega SOMENTE após interação significativa
        var events = ['scroll', 'click', 'touchstart'];
        var interactionCount = 0;
        var onInteraction = function() {
          interactionCount++;
          // Requer pelo menos 1 interação E o tempo mínimo
          if (interactionCount >= 1) {
            loadScript();
            events.forEach(function(e) {
              window.removeEventListener(e, onInteraction);
            });
          }
        };

        events.forEach(function(e) {
          window.addEventListener(e, onInteraction, { passive: true });
        });

        // Fallback absoluto: ${minDelay * 2}ms
        setTimeout(function() {
          loadScript();
        }, ${minDelay * 2});
      })();
    `,
    defer: true,
  };
}

export function conditionalLoadHeavy(
  condition: boolean,
  scriptRaw: string,
  minDelay?: number,
) {
  if (!condition) {
    return {};
  }

  return loadHeavy(scriptRaw, minDelay);
}
